import { useFormikContext } from 'formik';

import Grid from '@mui/material/Grid';

import { ensureDefined } from '@e-socna/utils';
import { ResolverReturnTypeEnum } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { QUANTITY_VALUE_FIELD_STEP } from '../../../constants/number-field-steps.constant';
import type { ResolvableFieldSettings } from '../../../components/ResolvableFields/types/resolvable-field-settings.type';

import { ResolvableField } from '../../ResolvableField';

import type { ResolvableServiceUsageForm_ResolvableServiceUsage } from '../fragments/resolvable-service-usage.fragment';

interface Props {
  readonly id: string;
  readonly resolvableFieldSettings: ResolvableFieldSettings;
}

export const ResolvableServiceUsageInfoFormStep = ({
  resolvableFieldSettings,
}: Readonly<Props>) => {
  const { values } =
    useFormikContext<ResolvableServiceUsageForm_ResolvableServiceUsage>();

  // data needed in form
  const referencesContext = useReferencesContext();
  const { unitsIdMap } = referencesContext;
  const { defaultUnit } = ensureDefined({
    defaultUnit: referencesContext.defaultUnit,
  });

  const fields = {
    showMaterialsInQuotation: {
      id: 'service-usage-settings-show-materials-in-quotation',
      name: 'settings.showMaterialsInQuotation',
      value: values.settings?.showMaterialsInQuotation,
    },
    showCostsInQuotation: {
      id: 'service-usage-settings-show-costs-in-quotation',
      name: 'settings.showCostsInQuotation',
      value: values.settings?.showCostsInQuotation,
    },
    showAmountsInQuotation: {
      id: 'service-usage-settings-show-amounts-in-quotation',
      name: 'settings.showAmountsInQuotation',
      value: values.settings?.showAmountsInQuotation,
    },
    showQuantitiesInQuotation: {
      id: 'service-usage-settings-show-quantities-in-quotation',
      name: 'settings.showQuantitiesInQuotation',
      value: values.settings?.showQuantitiesInQuotation,
    },
    quantityValue: {
      id: 'service-usage-quantity-value',
      name: 'quantity.value',
      value: values.quantity?.value,
    },
    quantityUnitId: {
      id: 'service-usage-quantity-unit',
      name: 'quantity.unitId',
      value: values.quantity?.unitId,
    },
  };

  return (
    <Grid container spacing={2} sx={{ alignContent: 'flex-start' }}>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <ResolvableField
          defaultStaticValue={1}
          id={fields.quantityValue.id}
          label="Quantité"
          name={fields.quantityValue.name}
          required
          resolvableFieldSettings={resolvableFieldSettings}
          slotProps={{
            htmlInput: {
              min: QUANTITY_VALUE_FIELD_STEP,
              step: QUANTITY_VALUE_FIELD_STEP,
            },
          }}
          type={ResolverReturnTypeEnum.Float}
          value={fields.quantityValue.value}
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <ResolvableField
          defaultStaticValue={defaultUnit._id}
          id={fields.quantityUnitId.id}
          idMap={unitsIdMap}
          label="Unité"
          name={fields.quantityUnitId.name}
          required
          resolvableFieldSettings={resolvableFieldSettings}
          type={ResolverReturnTypeEnum.UnitId}
          value={fields.quantityUnitId.value}
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <ResolvableField
          defaultStaticValue={false}
          id={fields.showMaterialsInQuotation.id}
          label="Afficher les matériels dans le devis ?"
          name={fields.showMaterialsInQuotation.name}
          required={false}
          resolvableFieldSettings={resolvableFieldSettings}
          type={ResolverReturnTypeEnum.Boolean}
          value={fields.showMaterialsInQuotation.value}
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <ResolvableField
          defaultStaticValue={false}
          id={fields.showCostsInQuotation.id}
          label="Afficher le détail des coûts dans le devis ?"
          name={fields.showCostsInQuotation.name}
          required={false}
          resolvableFieldSettings={resolvableFieldSettings}
          type={ResolverReturnTypeEnum.Boolean}
          value={fields.showCostsInQuotation.value}
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <ResolvableField
          defaultStaticValue
          id={fields.showAmountsInQuotation.id}
          label="Afficher les montants dans le devis ?"
          name={fields.showAmountsInQuotation.name}
          required={false}
          resolvableFieldSettings={resolvableFieldSettings}
          type={ResolverReturnTypeEnum.Boolean}
          value={fields.showAmountsInQuotation.value}
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <ResolvableField
          defaultStaticValue={false}
          id={fields.showQuantitiesInQuotation.id}
          label="Afficher les quantités dans le devis ?"
          name={fields.showQuantitiesInQuotation.name}
          required={false}
          resolvableFieldSettings={resolvableFieldSettings}
          type={ResolverReturnTypeEnum.Boolean}
          value={fields.showQuantitiesInQuotation.value}
        />
      </Grid>
    </Grid>
  );
};
